/**
 * Sprint T-16.1 · Automation Center · SchedulerTab.
 *
 * Vista declarativa exclusiva (§17.2 · sin next_run_at calculado).
 * Muestra schedule + estado + última ejecución de cada job.
 */
import React from "react";
import { Calendar, Info } from "lucide-react";
import { WidgetCard, EmptyState, LoadingState, CitationBadge, MetricCard, WidgetGrid } from "../advisor";
import { useAutomationInfo, useAutomationJobs } from "../../hooks/useAutomation";
import { StatusBadge, CategoryBadge } from "./shared/Badges";
import { RelativeTime, humanReadableSchedule } from "./shared/Formatters";


function SchedulerHealthStrip() {
  const q = useAutomationInfo();
  const d = q.data;
  const lastTick = d?.scheduler?.last_tick_at;
  const secsAgo = lastTick ? Math.max(0, Math.round((Date.now() - new Date(lastTick).getTime()) / 1000)) : null;
  const healthy = secsAgo != null && secsAgo < 60;
  return (
    <WidgetGrid cols="md:grid-cols-3">
      <MetricCard
        label="Estado scheduler"
        value={d?.running ? (healthy ? "OK" : "STALE") : "STOPPED"}
        hint={secsAgo != null ? `último tick hace ${secsAgo}s` : "—"}
        testId="scheduler-health-status"
      />
      <MetricCard
        label="Ticks acumulados"
        value={d?.scheduler?.tick_count ?? 0}
        hint="desde arranque"
        testId="scheduler-tick-count"
      />
      <MetricCard
        label="Ejecuciones procesadas"
        value={d?.worker?.processed ?? 0}
        hint="worker desde arranque"
        testId="scheduler-worker-processed"
      />
    </WidgetGrid>
  );
}


function UpcomingScheduleTable() {
  const q = useAutomationJobs();
  const items = q.data?.items || [];
  const nonManual = items.filter((j) => j.schedule?.kind !== "manual");

  return (
    <WidgetCard
      title={`Programación · ${nonManual.length} jobs recurrentes`}
      description="Vista declarativa · el cálculo del instante exacto se hace en el scheduler backend (T-16)."
      citations={["/api/automation/jobs", "schedule declarativo"]}
      testId="widget-scheduler-table"
    >
      {q.isLoading && <LoadingState />}
      {q.error && <EmptyState message="No se pudo cargar." />}
      {q.data && nonManual.length === 0 && (
        <EmptyState message="Sin jobs recurrentes." testId="scheduler-empty" />
      )}
      {nonManual.length > 0 && (
        <div className="-mx-1 overflow-x-auto">
          <table className="min-w-full text-xs" data-testid="scheduler-table">
            <thead className="border-b border-[var(--ag-border)] text-[10px] uppercase tracking-wide text-[var(--ag-text-dim)]">
              <tr>
                <th className="px-2 py-2 text-left">Job</th>
                <th className="px-2 py-2 text-left">Categoría</th>
                <th className="px-2 py-2 text-left">Schedule</th>
                <th className="px-2 py-2 text-left">Última ejecución</th>
                <th className="px-2 py-2 text-left">Estado</th>
                <th className="px-2 py-2 text-left">Próximo</th>
              </tr>
            </thead>
            <tbody>
              {nonManual.map((j) => {
                const state = j.state || {};
                const isPaused = state.paused_until && new Date(state.paused_until) > new Date();
                return (
                  <tr
                    key={j.job_id}
                    className="border-b border-[var(--ag-border)]/60"
                    data-testid={`scheduler-row-${j.job_id}`}
                  >
                    <td className="px-2 py-1.5 align-top">
                      <div className="font-mono text-[11px]">{j.name}</div>
                      <div className="text-[9px] text-[var(--ag-text-dim)]">{j.job_id}</div>
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <CategoryBadge category={j.category} />
                    </td>
                    <td className="px-2 py-1.5 align-top text-[11px]">
                      {humanReadableSchedule(j.schedule)}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <RelativeTime iso={state.last_run_at} />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      {state.last_status ? (
                        <StatusBadge status={state.last_status} />
                      ) : (
                        <span className="text-[10px] text-[var(--ag-text-dim)]">nunca</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      {isPaused ? (
                        <span className="text-[10px] text-slate-400">pausado</span>
                      ) : (
                        <span className="text-[10px] text-[var(--ag-text-dim)] italic">
                          pendiente
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </WidgetCard>
  );
}


export default function SchedulerTab() {
  return (
    <section className="space-y-4" data-testid="automation-scheduler-tab">
      <SchedulerHealthStrip />

      <div
        data-testid="scheduler-declarative-notice"
        className="rounded border border-[var(--ag-border)] bg-[var(--ag-surface-2)]/40 p-3 text-[11px] text-[var(--ag-text-dim)]"
      >
        <Info className="mr-1 inline h-3 w-3" />
        <strong>Nota T-16.1:</strong> esta vista es declarativa. La columna Próximo mostrará el
        instante exacto cuando el backend exponga <code className="font-mono">next_run_at</code> en{" "}
        <code className="font-mono">/api/automation/jobs</code> (planificado para T-16.2).
      </div>

      <UpcomingScheduleTable />

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["schedule declarativo", "next_run_at reservado T-16.2"]} />
      </footer>
    </section>
  );
}
